import {
  FieldMap,
  FieldTypes,
  FontId,
  PopulatedAssessment,
  ShortcutMap,
  TemplateFieldMap,
  TemplateKey,
} from '@hb/shared'
import { ReactEditor } from 'slate-react'
import { generatePDF } from '../../../backend/functions/other'
import { downloadFromStorage } from '../../../backend/storage/utils'
import { CustomEditor } from '../../../types/editor'
import { getPaymentsDropdown } from './cost'
import { insuranceItems } from './insurance'
import { midwifeItems, practiceItems } from './midwife'
import {
  consentFormPatientItems,
  dateShortcuts,
  getAuthShortcuts,
  patientItems,
  pregnancyItems,
  signerItems,
} from './shortcuts'

const templateFields: TemplateFieldMap = {
  isTemplateFieldMap: true,
  name: 'Input Fields',
  children: {
    initial: {
      isTemplateField: true,
      type: FieldTypes.INITIALS,
      placeholder: 'Initials',
      expectedInitials: '',
      patientName: '',
    },
    signature: {
      isTemplateField: true,
      type: FieldTypes.SIGNATURE,
      placeholder: 'Signature',
      expectedFirstName: '',
      expectedLastName: '',
      expectedMiddleInitial: '',
    },
    customField: {
      isCreateTemplateField: true,
    },
  },
}

export const shapeDropdownItems = {
  name: 'Shapes',
  children: {
    rectangle: {
      id: 'shape-rectangle',
      name: 'Rectangle',
      shortcut: true,
      isShapeItem: true,
      shapeType: 'rectangle',
    },
    square: {
      id: 'shape-square',
      name: 'Square',
      shortcut: true,
      isShapeItem: true,
      shapeType: 'square',
    },
    oval: { id: 'shape-oval', name: 'Oval', shortcut: true, isShapeItem: true, shapeType: 'oval' },
    circle: {
      id: 'shape-circle',
      name: 'Circle',
      shortcut: true,
      isShapeItem: true,
      shapeType: 'circle',
    },
  },
}

export const getDropdownItems = (
  assessment: PopulatedAssessment | null,
  templateType: TemplateKey,
): Record<string, ShortcutMap | FieldMap | TemplateFieldMap> => {
  switch (templateType) {
    case 'consentForm':
      return {
        patient: consentFormPatientItems,
        practice: practiceItems,
        pregnancy: pregnancyItems,
        insurance: insuranceItems,
        dateShortcuts,
        templateFields,
      }
    // practice contracts are populated with signer + practice shortcut args when sent
    // (createPracticeContractDocument) — no assessment/patient context, so only offer
    // groups that resolve from those
    case 'practiceContract':
      return {
        signer: signerItems,
        practice: practiceItems,
        dateShortcuts,
        templateFields,
      }
    default:
      return {
        patient: patientItems,
        pregnancy: pregnancyItems,
        midwife: midwifeItems,
        insurance: insuranceItems,
        cost: getPaymentsDropdown(assessment ?? undefined),
        authorizations: getAuthShortcuts(assessment ?? undefined),
        // ...nestedAssessmentFields.children,
        // calculations,
        dateShortcuts,
        // insuranceShortcuts,
        // signOn: signOnFields,
      }
  }
}

export const fontFamilyFromFontId = (fontId: FontId | 'TimesRoman') => {
  switch (fontId) {
    case 'Courier':
      return 'Courier New'
    case 'Helvetica':
      return 'Helvetica'
    case 'TimesRoman':
    case 'Times-Roman':
      return 'Times New Roman'
    case 'DancingScript':
      return 'Dancing Script'
    default:
      throw new Error('Invalid font id')
  }
}

export const downloadFromEditor = async (editor: CustomEditor) => {
  const node = ReactEditor.toDOMNode(editor, editor)
  if (node) {
    try {
      const { data } = await generatePDF({ html: node.innerHTML })
      const { path } = data
      if (path) {
        const res = await downloadFromStorage(path)
        return res
      }
      return { error: 'Error with response - no URL' }
    } catch (err) {
      console.error(err)
      return { error: `Internal error: ${(err as any).message}` }
    }
  } else {
    return { error: 'Internal error - no html' }
  }
}
